import React from 'react';
import { usePageBanner } from '../../ui/PageBannerProvider';

/**
 * Backs the single Refresh button in `ContactOptimizationNav`. Every
 * section endpoint is Postgres-cached server-side for hours (see
 * backend's `cache.py`) -- clicking Refresh needs to force a live
 * recompute of *everything currently on screen*, not just the page's one
 * main fetch. Components register their own reload function here on
 * mount (and unregister on unmount), so the button never has to know the
 * current tab's specific fetch calls -- it just re-runs whatever's
 * actually mounted right now.
 *
 * Scoped per-page (each page wraps its own JSX in `RefreshProvider`), so
 * switching tabs always gets a fresh, empty registry instead of carrying
 * over a previous tab's handlers.
 */
interface RefreshContextValue {
  refreshing: boolean;
  triggerRefresh: () => Promise<void>;
  register: (handler: () => Promise<unknown>) => () => void;
  /** ISO timestamp the current tab's primary section was last actually
   * computed (cached or fresh) -- see `useReportFreshness`. */
  lastUpdated: string | null;
  reportFreshness: (iso: string | null | undefined) => void;
}

const RefreshContext = React.createContext<RefreshContextValue | null>(null);

/** The one wording used for "this page is re-fetching", wherever that is
 * raised. Exported so a page refetching for its own reasons (Scorecard
 * changing period, say) says the same thing as the Refresh button rather
 * than inventing a second phrase for the same state. */
export const REFRESHING_BANNER_MESSAGE = 'Refreshing data from the warehouse\u2026';

export function RefreshProvider({ children }: { children: React.ReactNode }) {
  const [refreshing, setRefreshing] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<string | null>(null);
  const handlersRef = React.useRef<Set<() => Promise<unknown>>>(new Set());

  const register = React.useCallback((handler: () => Promise<unknown>) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const triggerRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all(Array.from(handlersRef.current).map((h) => h()));
    } finally {
      setRefreshing(false);
    }
  }, []);

  const reportFreshness = React.useCallback((iso: string | null | undefined) => {
    setLastUpdated(iso ?? null);
  }, []);

  const value = React.useMemo(
    () => ({ refreshing, triggerRefresh, register, lastUpdated, reportFreshness }),
    [refreshing, triggerRefresh, register, lastUpdated, reportFreshness],
  );

  // Same global status strip Dialing Opportunity and Lead-to-Cash already
  // use for "reloading in the background" (see DialingOpportunityPage.tsx)
  // -- one consistent place in the UI users already know to look for "is
  // this page currently re-fetching," instead of a second, Contact
  // Optimization-only loading indicator.
  usePageBanner(refreshing ? { message: REFRESHING_BANNER_MESSAGE, tone: 'info' } : null);

  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>;
}

/** Used by `ContactOptimizationNav` to render/drive the Refresh button. */
export function useRefreshController(): RefreshContextValue | null {
  return React.useContext(RefreshContext);
}

/**
 * Registers `reload` (re-run on every Refresh click) for as long as the
 * calling component is mounted. Always calls the *latest* `reload` --
 * pass it straight from a `useCallback` above; no need to also add it as
 * a dependency here, since a fresh ref update is enough and re-running
 * this effect on every reload-identity change would otherwise
 * register/unregister on every render for callbacks that aren't
 * memoized upstream.
 */
export function useRefreshRegistration(reload: () => Promise<unknown>) {
  const ctx = useRefreshController();
  const reloadRef = React.useRef(reload);
  reloadRef.current = reload;

  React.useEffect(() => {
    if (!ctx) return;
    return ctx.register(() => reloadRef.current());
  }, [ctx]);
}

/**
 * Tells the tab bar's "Updated ..." label when the page's primary section
 * was actually computed -- pass the `computed_at` the backend already
 * stamps on every cached-or-fresh payload (see `SectionCacheMeta`). Only
 * the one main section per tab reports here (Pulse's summary, Timing's
 * heatmap, Plan's capacity, Queue's queue) -- deliberately not every
 * secondary fetch on the page, so the label answers one clear question
 * ("how current is what I'm looking at") instead of listing a timestamp
 * per section.
 */
export function useReportFreshness(computedAt: string | null | undefined) {
  const ctx = useRefreshController();
  React.useEffect(() => {
    ctx?.reportFreshness(computedAt);
  }, [ctx, computedAt]);
}
